import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';

const loaderStyle = {
  display: 'block',
  margin: '0 auto',
  borderColor: 'rgb(0, 43, 54)',
};

const TOPIC_CYCLE_SPEED_MS = 1500;
const TOPIC_FONT_SIZE = '2rem';

// 'sequential' (random start, then ordered) or 'random' (new random each tick)
const ROTATION_MODE = 'random';

// One-to-one topic/color mapping
const TOPIC_COLOR_PAIRS = [
  { topic: 'python',                      color: '#59c6c1' },
  { topic: 'sql',                         color: '#ced689' },
  { topic: 'time-series analysis',        color: '#ff7a59' },
  { topic: 'machine learning',            color: '#00b894' },
  { topic: 'embeddings',                  color: '#0984e3' },
  { topic: 'deep learning',               color: '#e84393' },
  { topic: 'natural language processing', color: '#f9ca24' },
  { topic: 'data visualization',          color: '#6c5ce7' },
  { topic: 'statistics',                  color: '#fd79a8' },
  { topic: 'cloud computing',             color: 'gray' },
  { topic: 'APIs',                        color: '#d63031' },
  { topic: 'big data',                    color: '#fab1a0' },
  { topic: 'data engineering',            color: '#636e72' },
];

export default function Welcome() {
  const navigate = useNavigate();

  // Randomize initial topic so the first one isn't always the same
  const [idx, setIdx] = useState(() =>
    Math.floor(Math.random() * TOPIC_COLOR_PAIRS.length)
  );
  const [loading, setLoading] = useState(true);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (ROTATION_MODE === 'random') {
        setIdx((prev) => {
          let next;
          do {
            next = Math.floor(Math.random() * TOPIC_COLOR_PAIRS.length);
          } while (next === prev && TOPIC_COLOR_PAIRS.length > 1);
          return next;
        });
      } else {
        setIdx((p) => (p + 1) % TOPIC_COLOR_PAIRS.length);
      }
      setAnimKey((k) => k + 1);
    }, TOPIC_CYCLE_SPEED_MS);

    return () => clearInterval(interval);
  }, []);

  const { topic, color } = TOPIC_COLOR_PAIRS[idx] ?? TOPIC_COLOR_PAIRS[0];

  const handleTopicClick = () => {
    navigate(`/search/?search=${encodeURIComponent(topic)}`);
  };

  const handleTopicKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTopicClick();
    }
  };

  return (
    <div
      className="welcome"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '150px 16px 32px',
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      {/* CSS scoped to .welcome to avoid leaking into navbar */}
      <style>{`
        .welcome @keyframes popIn {
          0% { opacity: 0; transform: translateY(4px) scale(0.95); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .welcome .topic-anim { animation: none !important; }
        }
        .welcome .btn {
          border: none;
          border-radius: 6px;
          padding: 8px 14px;
          font-weight: 700;
          cursor: pointer;
        }
        .welcome .btn-primary {
          background: rgb(89, 198, 193);
          color: white;
        }
        .welcome .btn-dark {
          background: rgb(0, 43, 54);
          color: white;
        }
      `}</style>

      <div style={{ maxWidth: 900, width: '100%' }}>
        {loading ? (
          <ClipLoader cssOverride={loaderStyle} size={150} color="#123abc" loading />
        ) : (
          <>
            <h4 style={{ color: 'gray', marginTop: 0 }}>Welcome to Inertia7!</h4>

            <h1 style={{ marginTop: 10, marginBottom: 16 }}>
              <strong>Write</strong> and <strong>publish</strong>{' '}
              <span
                style={{
                  backgroundColor: 'black',
                  borderRadius: 3,
                  color: 'white',
                  padding: '0px 6px',
                }}
              >
                technical documentation
              </span>{' '}
              <strong>projects</strong>!
            </h1>

            {/* Rotating clickable topic */}
            <div
              key={animKey}
              className="topic-anim"
              role="button"
              tabIndex={0}
              onClick={handleTopicClick}
              onKeyDown={handleTopicKeyDown}
              title={`Search for ${topic}`}
              style={{
                display: 'inline-block',
                animation: 'popIn 300ms ease-out',
                margin: '8px 0 20px',
                lineHeight: 1.25,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <span style={{ color: '#666', fontSize: TOPIC_FONT_SIZE, fontWeight: 700 }}>(</span>
              <span style={{ color, fontSize: TOPIC_FONT_SIZE, fontWeight: 800 }}>{topic}</span>
              <span style={{ color: '#666', fontSize: TOPIC_FONT_SIZE, fontWeight: 700 }}>)</span>
            </div>

            <hr />

            <div style={{ marginTop: 16 }}>
              <Link to="/accounts/signin/" style={{ textDecoration: 'none' }}>
                <button className="btn btn-primary">
                  <strong>Sign In</strong>
                </button>
              </Link>
              <span style={{ padding: '0 10px' }}>or</span>
              <Link to="/accounts/register/" style={{ textDecoration: 'none' }}>
                <button className="btn btn-dark">
                  <strong>Join, it's free!</strong>
                </button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
